import { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { chipColors } from '../../../lib/contrast';
import { Box, Chip, Tooltip, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AppContext,
    SttChipFacilityRow,
    SttButtonFab,
    SttPage,
} from '../../../sporttia/all';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import SttCachedTable from '../../../sporttia/SttCachedTable';
import { useQueryFilters } from '../../../lib/hooks';
import { SPORTCENTER_FREE_SCHEDULES } from '../../../lib/queryKeys';
import FreeSchedule from './FreeSchedule';
import useScsService from '../../../services/ScsService';
import { getPath } from '../../Pages';
import { useFreeSchedulesTour } from 'src/lib/tours/useFreeSchedulesTour';
import { PERMISSIONS } from '../../../lib/permissions';

interface Facility {
    id: string | number;
    name: string;
}

interface FreeScheduleItem {
    id: number;
    layer?: string;
    name: string;
    preBookingIni?: string;
    ini: string;
    end: string;
    months: number[];
    facilities: Facility[];
}

interface Filters extends Record<string, unknown> {
    name?: string;
    isActive?: boolean;
    trash?: boolean;
}

// Sólo el fondo: el color del rótulo lo calcula `chipColors`, que elige tinta
// o blanco según lo que se lea. Con el blanco fijo, las capas 1, 2 y 3 se
// quedaban en 2,78:1, 3,12:1 y 2,16:1.
const LAYER_COLORS: Record<string, string> = {
    B: '#757575', // Gray for Base
    '1': '#4caf50', // Green for layer 1
    '2': '#2196f3', // Blue for layer 2
    '3': '#ff9800', // Orange for layer 3
    '4': '#9c27b0', // Purple for layer 4
    '5': '#e91e63', // Pink for layer 5
};

interface LayerChipProps {
    layer: string;
}

function LayerChip({ layer }: LayerChipProps) {
    const { t } = useTranslation();
    const fondo = LAYER_COLORS[layer] ?? LAYER_COLORS.B!;

    return (
        <Chip
            size="small"
            label={layer === 'B' ? t('Base') : layer}
            sx={{
                ...chipColors(fondo),
                fontWeight: 600,
                minWidth: 40,
            }}
        />
    );
}

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

interface MonthsVisualizationProps {
    months: number[];
}

function MonthsVisualization({ months }: MonthsVisualizationProps) {
    const { t } = useTranslation();

    if (months.length === 12) {
        return (
            <Box sx={{ fontSize: '0.875rem', color: 'success.main' }}>
                {t('general.all')}
            </Box>
        );
    }

    return (
        <Box display="flex" gap={0.25} justifyContent="center">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                const isActive = months.includes(m);
                return (
                    <Tooltip
                        key={m}
                        title={t(`month.${MONTH_NAMES[m - 1]}`)}
                        arrow
                    >
                        <Box
                            sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.625rem',
                                fontWeight: 600,
                                backgroundColor: isActive
                                    ? 'primary.main'
                                    : 'var(--muted)',
                                // Sobre el lima va la tinta: en blanco la
                                // inicial se quedaba en 1,3:1 y no se leía. Y
                                // el mes inactivo pasa del `grey.500` sobre
                                // `grey.200` (2,31:1) al par de tokens
                                // apagados, que sí cambian con el modo: el
                                // gris fijo dejaba la insignia clara con letra
                                // clara en tema oscuro.
                                color: isActive
                                    ? 'primary.contrastText'
                                    : 'var(--muted-foreground)',
                                cursor: 'default',
                            }}
                        >
                            {t(`month.initial.${m}`)}
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

export default function FreeSchedules() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id: urlId } = useParams<{ id: string }>();
    const { startTour } = useFreeSchedulesTour();
    const [freeScheduleId, setFreeScheduleId] = useState<{
        id: number | 'create';
    } | null>(null);
    const scsServices = useScsService();
    const [filters, setFilters] = useQueryFilters<Filters>(
        { isActive: true },
        SPORTCENTER_FREE_SCHEDULES,
    );

    // Handle free schedule ID from URL path parameter (e.g., /frees/schedules/123)
    useEffect(() => {
        if (urlId) {
            setFreeScheduleId({ id: Number(urlId) });
        }
    }, [urlId]);

    const handleFacilityClick = useCallback(
        (facility: Facility) => {
            navigate(`${getPath('facilities')}?facilityId=${facility.id}`);
        },
        [navigate],
    );

    const openFreeSchedule = useCallback((row: FreeScheduleItem) => {
        setFreeScheduleId({ id: row.id });
    }, []);

    const closeFreeSchedule = useCallback(() => {
        setFreeScheduleId(null);
        // Navigate back to free schedules list if we came from a direct URL
        if (urlId) {
            navigate('/frees/schedules');
        }
    }, [urlId, navigate]);

    const createFreeSchedule = useCallback(() => {
        setFreeScheduleId({ id: 'create' });
    }, []);

    const handleFilter = useCallback(
        (value: Filters) => {
            setFilters(value);
        },
        [setFilters],
    );

    const queryFn = useCallback(
        (params: Record<string, unknown>) =>
            scsServices.getFreeSchedules(Number(cxt?.sc?.id) || 0, params),
        [cxt?.sc?.id, scsServices],
    );

    const columns = useMemo(
        () => [
            {
                title: t('Layer'),
                field: 'layer',
                value: (row: FreeScheduleItem) => (
                    <LayerChip layer={row.layer || 'B'} />
                ),
            },
            {
                title: t('Name'),
                field: 'name',
                align: 'left' as const,
                value: (row: FreeScheduleItem) => (
                    <Link
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => openFreeSchedule(row)}
                    >
                        {row.name}
                    </Link>
                ),
            },
            {
                type: 'duration' as const,
                title: t('Advance'),
                value: (row: FreeScheduleItem) =>
                    row.preBookingIni?.startsWith('0000-00-00')
                        ? ''
                        : row.preBookingIni,
            },
            {
                type: 'period' as const,
                title: t('Period'),
                value: (row: FreeScheduleItem) => ({
                    ini: row.ini,
                    end: row.end,
                }),
            },
            {
                title: t('Months'),
                value: (row: FreeScheduleItem) => (
                    <MonthsVisualization months={row.months} />
                ),
            },
            {
                title: t('Facilities'),
                value: (row: FreeScheduleItem) => (
                    <SttChipFacilityRow
                        facilities={row.facilities}
                        onFacilityClick={handleFacilityClick}
                    />
                ),
            },
        ],
        [t, openFreeSchedule, handleFacilityClick],
    );

    return (
        <SttPage title={t('Schedules')} help={startTour}>
            <SttTopFilteringControls
                p={2}
                fields={[
                    {
                        caption: t('Name'),
                        name: 'name',
                        type: 'text',
                        value: filters.name,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                extraFields={[
                    {
                        caption: t('Active'),
                        name: 'isActive',
                        type: 'check',
                        checked: filters.isActive,
                    },
                ]}
                onFilter={handleFilter}
                trashAction
            />

            <SttCachedTable
                queryKey={SPORTCENTER_FREE_SCHEDULES}
                queryFn={queryFn}
                queryParams={filters}
                prefetching={false}
                columns={columns}
            />

            {freeScheduleId && (
                <FreeSchedule
                    idFreeSchedule={freeScheduleId.id}
                    onClose={closeFreeSchedule}
                />
            )}

            <SttButtonFab
                onClick={createFreeSchedule}
                permission={PERMISSIONS.SCHEDULE.CREATE}
            />
        </SttPage>
    );
}
